import axios from 'axios';

const SESSION_EXPIRED_STATUSES = [401, 419];
const TOAST_DEDUP_MS = 2500;

const ACTION_RULES = [
    { pattern: /\/conversation\/[^/]+\/[^/]+\/private\/end$/, action: 'make space public' },
    { pattern: /\/conversation\/[^/]+\/[^/]+\/private\/getToken$/, action: 'join this private space' },
    { pattern: /\/conversation\/[^/]+\/[^/]+\/private$/, action: 'make space private' },
    { pattern: /\/conversation\/create$/, action: 'create space' },
    { pattern: /\/conversation\/bulk\/create$/, action: 'create spaces' },
    { pattern: /\/conversation\/name\//, action: 'rename space' },
    { pattern: /\/conversation\/delete\//, action: 'delete space' },
    { pattern: /\/conversation\/(?:pin|unpin)\//, action: 'pin space' },
    { pattern: /\/spaces\/(?:pin|unpin)$/, action: 'pin space' },
    { pattern: /\/clock-out$/, action: 'clock out' },
    { pattern: /\/clock-in$/, action: 'clock in' },
    { pattern: /\/moderator\/add$/, action: 'add moderator' },
    { pattern: /\/moderator\/delete$/, action: 'remove moderator' },
    { pattern: /\/preferences\/.+\/update$/, action: 'save preferences' },
    { pattern: /\/services\/recording\/new$/, action: 'start recording' },
    { pattern: /\/services\/stream/, action: 'start stream' },
    { pattern: /\/addon\/kino-recorder$/, action: 'use the recorder' },
    { pattern: /\/chat\/images\//, action: 'load this image' },
];

const IGNORE_PATHS = [
    /\/login$/,
    /\/logout$/,
    /\/register$/,
    /\/signup$/,
    /\/auth\/signin/,
    /\/auth\/signout/,
    /\/services\/recording\/status$/,
    /\/preferences\/all$/,
    /\/workspace\/[^/]+\/access-check$/,
    /\/room\/init\//,
    /\/conversations\/[^/]+$/,
    /\/kino\/[^/]+\/permissions$/,
    /\/spaces\/list$/,
];

let lastToastAt = 0;

export function getRequestPath(error) {
    const raw = error?.config?.url || '';
    try {
        return new URL(raw, window.location.origin).pathname;
    } catch (e) {
        return String(raw).split('?')[0];
    }
}

export function getSessionExpiredAction(pathname) {
    const rule = ACTION_RULES.find((item) => item.pattern.test(pathname));
    return rule ? rule.action : 'complete this action';
}

export function shouldIgnoreSessionExpiredRequest(error) {
    const method = String(error?.config?.method || 'get').toLowerCase();
    if (method === 'get') {
        return true;
    }
    const pathname = getRequestPath(error);
    return IGNORE_PATHS.some((pattern) => pattern.test(pathname));
}

export function isSessionExpiredStatus(error) {
    const status = error?.response?.status;
    return SESSION_EXPIRED_STATUSES.includes(status);
}

export function buildSessionExpiredMessage(pathname) {
    return `Cannot ${getSessionExpiredAction(pathname)}. Your session has expired, please sign in again.`;
}

export function joinedAsSignedInUser(userId) {
    return Number(userId) > 0;
}

export function shouldShowSessionExpiredToast(error, { isJoined, joinedAsSignedInUser: wasSignedIn } = {}) {
    if (!isSessionExpiredStatus(error)) {
        return false;
    }
    if (shouldIgnoreSessionExpiredRequest(error)) {
        return false;
    }
    if (typeof isJoined === 'function' && !isJoined()) {
        return false;
    }
    if (typeof wasSignedIn === 'function' && !wasSignedIn()) {
        return false;
    }
    return true;
}

export function installSessionExpiredInterceptor({ isJoined, joinedAsSignedInUser: wasSignedIn, showNotification }) {
    if (axios.__sessionExpiredInterceptorInstalled) {
        return;
    }
    axios.__sessionExpiredInterceptorInstalled = true;
    axios.defaults.headers.common['Accept'] = 'application/json';

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            if (shouldShowSessionExpiredToast(error, { isJoined, joinedAsSignedInUser: wasSignedIn })) {
                const now = Date.now();
                if (now - lastToastAt >= TOAST_DEDUP_MS) {
                    lastToastAt = now;
                    const message = buildSessionExpiredMessage(getRequestPath(error));
                    if (typeof showNotification === 'function') {
                        showNotification(
                            '<i class="fas fa-exclamation-triangle"></i>',
                            message,
                            'sessionExpired'
                        );
                    }
                }
            }
            return Promise.reject(error);
        }
    );
}
